import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import portfolioService from '../../services/portfolioService';
import type { PortfolioKpis, PortfolioMap } from '../../types/portfolio';
import { PortfolioKpiTiles } from '../Portfolio/PortfolioKpiTiles';
import { SemanticMap } from '../Portfolio/SemanticMap';

const Header: React.FC<{ title: string }> = ({ title }) => {
  const { t } = useTranslation();
  return (
    <div className="mb-4 flex items-center justify-between gap-3">
      <h3 className="text-lg font-semibold text-foreground">{title}</h3>
      <Link to="/portefeuille" className="text-sm text-primary hover:underline">
        {t('portfolio.widgets.open')}
      </Link>
    </div>
  );
};

const Loading: React.FC = () => {
  const { t } = useTranslation();
  return (
    <p className="flex items-center gap-2 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {t('portfolio.loading')}
    </p>
  );
};

/** Dashboard widget `projects-kpis`: the six portfolio KPIs, compact (plan §9.5, lot L4). */
export const PortfolioKpisWidget: React.FC = () => {
  const { t } = useTranslation();
  const [kpis, setKpis] = useState<PortfolioKpis | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    portfolioService
      .getKpis()
      .then((k) => !cancelled && setKpis(k))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <Header title={t('portfolio.widgets.kpis')} />
      {kpis ? (
        <PortfolioKpiTiles kpis={kpis} compact />
      ) : failed ? (
        <p className="text-sm text-muted-foreground">{t('portfolio.loadError')}</p>
      ) : (
        <Loading />
      )}
    </div>
  );
};

/** Dashboard widget `portfolio-minimap`: the map with its motifs, not interactive (plan §9.5, lot L4). */
export const PortfolioMinimapWidget: React.FC = () => {
  const { t } = useTranslation();
  const [map, setMap] = useState<PortfolioMap | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    portfolioService
      .getMap()
      .then((m) => !cancelled && setMap(m))
      .catch(() => !cancelled && setFailed(true));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <Header title={t('portfolio.widgets.minimap')} />
      {failed ? (
        <p className="text-sm text-muted-foreground">{t('portfolio.loadError')}</p>
      ) : !map ? (
        <Loading />
      ) : map.points.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('portfolio.map.empty')}</p>
      ) : (
        <Link to="/portefeuille" aria-label={t('portfolio.widgets.open')} className="block">
          <SemanticMap
            points={map.points}
            motifs={map.motifs}
            mode="motif"
            compact
            className="pointer-events-none relative h-56 w-full overflow-hidden rounded-lg border border-border"
          />
          <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
            {[
              t(map.points.length > 1 ? 'portfolio.insight.projectsMany' : 'portfolio.insight.projectsOne', {
                n: map.points.length,
              }),
              t(map.motifs.length > 1 ? 'portfolio.insight.motifsMany' : 'portfolio.insight.motifsOne', {
                n: map.motifs.length,
              }),
              map.insight ?? '',
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </Link>
      )}
    </div>
  );
};
